import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Footer } from "@/components/layout/Footer";
import { useAuth } from "@/features/auth/useAuth";
import {
  DEPARTMENT_LABEL,
  ROLE_LABEL,
  STAFF_LEVEL_LABEL,
  type AppUser,
} from "@/features/auth/types";
import { company } from "@/content/site";

interface NavItem {
  label: string;
  to: string;
}

export function AppLayout({ nav, title }: { nav: NavItem[]; title: string }) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  async function handleSignOut() {
    await signOut();
    navigate("/app");
  }

  return (
    <div className="flex min-h-dvh min-w-0 flex-col md:flex-row">
      <aside className="sticky top-0 z-30 border-b border-brand-100 bg-brand-900 text-brand-100 md:static md:w-64 md:border-b-0 md:border-r md:border-brand-800">
        <div className="flex items-center justify-between p-4">
          <Link to="/" className="flex min-w-0 items-center gap-2 font-bold text-white" aria-label={`${company.name} home`}>
            <span className="inline-flex items-center rounded-lg bg-white px-1.5 py-1">
              <img src="/brand/logo.png" alt={company.name} className="h-7 w-auto" />
            </span>
            <span className="truncate text-sm">{title}</span>
          </Link>
        </div>
        <nav className="flex gap-1 overflow-x-auto px-2 pb-3 [scrollbar-width:thin] md:flex-col md:overflow-visible">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end
              className={({ isActive }) =>
                cn(
                  "whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium text-brand-200 hover:bg-brand-800",
                  isActive && "bg-brand-800 text-white",
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex flex-col gap-3 border-b border-brand-100 bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          {user ? (
            <div className="flex min-w-0 items-center gap-3">
              <span
                className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-brand-900 text-sm font-semibold text-accent-400"
                aria-hidden
              >
                {initials(user.name || user.email)}
              </span>
              <div className="min-w-0 leading-tight">
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <span className="truncate text-sm font-semibold text-brand-900">
                    {user.name || user.email.split("@")[0]}
                  </span>
                  <span className="shrink-0 rounded-full bg-accent-400/20 px-2 py-0.5 text-xs font-medium text-accent-600">
                    {roleDescriptor(user)}
                  </span>
                </div>
                <div className="truncate text-xs text-brand-500">{user.email}</div>
              </div>
            </div>
          ) : (
            <div />
          )}
          <Button variant="ghost" size="sm" className="w-full justify-center sm:w-auto" onClick={handleSignOut}>
            <LogOut className="h-4 w-4" /> Sign out
          </Button>
        </header>

        <main className="container min-w-0 flex-1 py-6 sm:py-8">
          <Outlet />
        </main>

        <Footer className="mt-0" />
      </div>
    </div>
  );
}

/** Human label for the badge: department + tier for staff, else the role. */
function roleDescriptor(user: AppUser): string {
  if (user.role !== "internal") return ROLE_LABEL[user.role];
  if (user.staffLevel === "admin") return "Admin";
  if (user.staffLevel && user.department) {
    return `${DEPARTMENT_LABEL[user.department]} ${STAFF_LEVEL_LABEL[user.staffLevel]}`;
  }
  return ROLE_LABEL.internal;
}

function initials(value: string): string {
  const parts = value.replace(/@.*/, "").split(/[\s._-]+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
